import { map, omit, uniqueId } from 'lodash-es'
import { writable } from 'svelte/store'
import { isNonEmptyArray } from '#app/lib/boolean_tests'
import { newError, type ContextualizedError } from '#app/lib/error'
import { objectEntries } from '#app/lib/utils'
import type { SerializedEntity } from '#entities/lib/entities'
import type { InvClaimValue, PropertyUri } from '#server/types/entity'
import { i18n } from '#user/lib/i18n'
import type { WikimediaLanguageCode } from 'wikibase-sdk'

export type EditorLabelValue = string | symbol
export type EditorLabels = Partial<Record<WikimediaLanguageCode, EditorLabelValue>>

export interface PropertyEditor {
  id: string
  value: InvClaimValue | symbol
}
export type ClaimsEditors = Record<PropertyUri, PropertyEditor[]>

export type EditorClaimValue <T extends InvClaimValue = InvClaimValue> = T | symbol
export type EditorSerializedEntity = SerializedEntity & { labels: EditorLabels, claimsEditors: ClaimsEditors }

export function getEditableLabels (labels: SerializedEntity['labels']) {
  return omit(labels, 'fromclaims')
}

export function alphabeticallySortedEntries (labels: EditorLabels) {
  return objectEntries(labels)
  .sort((a, b) => a[0] > b[0] ? 1 : -1)
}

export function getNonEmptyPropertyClaims (propertyClaims: EditorClaimValue[] = []) {
  return propertyClaims
  .filter(isNonEmptyClaimValue)
}

export function getPropertyClaimsCount (propertyClaims: EditorClaimValue[]) {
  return getNonEmptyPropertyClaims(propertyClaims).length
}

export function getPropertyClaimsCountFromEditors (propertyClaimsEditors: PropertyEditor[]) {
  const propertyClaims = map(propertyClaimsEditors, 'value')
  return getNonEmptyPropertyClaims(propertyClaims).length
}

export function isEmptyClaimValue (value: EditorClaimValue) {
  return value === null || value === Symbol.for('removed') || value === Symbol.for('moved')
}

export function isNonEmptyClaimValue (value: EditorClaimValue): value is Exclude<EditorClaimValue, null | symbol> {
  return value != null && !isEmptyClaimValue(value)
}

export function buildPropertyEditor (value: PropertyEditor['value']): PropertyEditor {
  return { id: uniqueId('editor'), value } as PropertyEditor
}

export function addClaimValue (propertyEditors: PropertyEditor[] = [], value: EditorClaimValue) {
  const hasValue = propertyEditors.find(({ value: val }) => val === value)
  if (!hasValue) propertyEditors.push(buildPropertyEditor(value))
  return propertyEditors
}

export function getPropertyClaimsValuesFromEditors (propertyEditors: PropertyEditor[] = []) {
  return map(propertyEditors, 'value').filter(isNonEmptyClaimValue)
}

export function getClaimsValuesFromClaimsEditors (claimsEditors: Record<PropertyUri, PropertyEditor[]> = {}) {
  const claims: SerializedEntity['claims'] = {}
  for (const [ property, claimEditor ] of objectEntries(claimsEditors)) {
    const propertyClaims = getPropertyClaimsValuesFromEditors(claimEditor)
    if (isNonEmptyArray(propertyClaims)) {
      claims[property] = propertyClaims
    }
  }
  return claims
}

export const currentEditorKey = writable(null)

export function errorMessageFormatter (err: ContextualizedError) {
  if (err.message.includes('this property value is already used')) {
    // @ts-expect-error
    const uri = err.responseJSON.context.entity
    err.html = `${i18n('this property value is already used')}: <a href="/entity/${uri}" class="link">${uri}</a>`
  }
  if (err.statusCode === 413) {
    const cause = err
    err = newError('image is too big', 413)
    if (cause.stack) err.stack = cause.stack
    err.cause = cause
  }
  return err
}
